import { useUserSettingsDefaultsQuery } from "../queries";

/**
 * Has the user's settings read come back yet?
 *
 * Not the same question as "does this user have a saved X". A setting the user
 * has never opened falls back to its factory default, and the value that lands
 * in the context is then indistinguishable from a stored row carrying the same
 * value — so nothing about a setting's own shape can answer this. Code that
 * needs to avoid recording a fallback as the user's choice has to ask the read,
 * not the value: `useInitialUserContextLoader` keeps exactly this as its own
 * `settingsLoaded` state, but does not publish it.
 *
 * Reads the shared query's status without asking for a fetch of its own. Many
 * callers already subscribe to this key with fetching on, so the request is not
 * this hook's to make or to wait for; `isAutoFetching: false` keeps it a pure
 * observer, which is all it needs to be. React-query keys the state rather than
 * the caller, so every subscriber sees the same status and none of them costs
 * an extra request.
 *
 * A failed read counts as answered. Nothing more is coming, the context keeps
 * its defaults, and a caller that waits for ever is a worse outcome than one
 * that proceeds with the same values the user is already being shown. An idle
 * query — nothing anywhere has enabled it yet — reads as not answered, which is
 * the cautious side and what a caller wanting to avoid recording a fallback
 * wants.
 */
export default function useHaveUserSettingsAnswered(): boolean {
  const { isSuccess, isError } = useUserSettingsDefaultsQuery({ isAutoFetching: false });
  return isSuccess || isError;
}
